import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/supabase';
import { ChevronDown, Settings, User, LogOut, MessageSquare, HelpCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AppearancePicker from './AppearancePicker';
import EditProfileModal from './EditProfileModal';
import FeedbackModal from './FeedbackModal';
import { clearPersistedCache } from '../utils/queryCache';
import { turnOnTours } from '../tours/tourPrefs';
import { startActiveTour } from '../tours/activeTour';

const MENU_WIDTH = 208;

const ProfileDropdown = ({ variant = 'full' }) => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const userName = userData?.first_name || '';

  const [isOpen, setIsOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Recompute menu position when opening or on viewport/scroll changes
  const updatePos = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    // Right-aligned to the button. MENU_WIDTH is shared with the style below
    // so the two cannot drift: when the menu grew for the appearance picker,
    // a hardcoded number here would have left it hanging off the edge.
    setPos({ top: r.bottom + 8, left: Math.max(8, r.right - MENU_WIDTH) });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePos();
    window.addEventListener('scroll', updatePos, { capture: true });
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, { capture: true });
      window.removeEventListener('resize', updatePos);
    };
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setIsOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async (e) => {
    e.stopPropagation();
    try {
      await supabase.auth.signOut();
      // League data cached on disk outlives the session that was allowed to
      // read it, and the next person to open the app may not be in that
      // league.
      clearPersistedCache();
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const getInitial = () => (userName ? userName[0].toUpperCase() : 'U');

  return (
    <>
      <div style={{ position: 'relative' }}>
        {variant === 'icon' ? (
          <button
            ref={btnRef}
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsOpen(o => !o); }}
            aria-label="Settings"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 44, height: 44,
              border: 'none', background: 'none', cursor: 'pointer',
              color: 'var(--color-text-strong)', WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Settings size={22} strokeWidth={1.8} />
          </button>
        ) : (
          <button
            ref={btnRef}
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsOpen(o => !o); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
              backgroundColor: 'var(--color-bg-page)', border: '1.5px solid var(--color-border)',
              transition: 'border-color 0.15s',
            }}
            aria-haspopup="menu"
            aria-expanded={isOpen}
          >
            {/* Avatar */}
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, var(--color-brand), #3B82F6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{getInitial()}</span>
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)' }}>{userName}</span>
            <ChevronDown size={14} color="var(--color-text-muted)" style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
        )}
      </div>

      {/* Portal — backdrop + menu rendered at body level to avoid clipping */}
      {/* The wrapper carries .theme-auto for the same reason ModalPortal does:
          a portal mounts at document.body, outside the page that opened it, so
          without it the menu resolves every token to its light value and opens
          white on a dark page. This one portals directly rather than through
          ModalPortal, so it needs its own. */}
      {isOpen && createPortal(
        <div className="theme-auto">
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setIsOpen(false)} />
          <div
            role="menu"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed', zIndex: 9999,
              top: pos.top, left: pos.left,
              width: MENU_WIDTH,
              backgroundColor: 'var(--color-bg-card)',
              border: '1.5px solid var(--color-border)',
              borderRadius: 10,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              padding: '4px 0',
            }}
          >
            {/* Above the actions, and it does not close the menu: changing
                appearance is something you look at while you change it, and a
                menu that shut on the first tap would hide the result. */}
            <AppearancePicker />
            <div style={{ height: 1, backgroundColor: 'var(--color-border)', margin: '2px 0 4px' }} />

            <button
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setShowEditModal(true); setIsOpen(false); }}
              style={{ width: '100%', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-bg-page)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <User size={14} color="var(--color-text-muted)" />
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-strong)' }}>Edit Profile</span>
            </button>

            {/* Between the profile and logout, because it is a thing you do
                with your account rather than a destination. The support page
                still carries the email address for anyone who would rather
                write one. */}
            <button
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setShowFeedback(true); setIsOpen(false); }}
              style={{ width: '100%', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-bg-page)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <MessageSquare size={14} color="var(--color-text-muted)" />
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-strong)' }}>Send Feedback</span>
            </button>

            {/* Tours run unprompted only for accounts newer than the tours
                (tours/tourPrefs.js). This is how everyone else gets them: the
                page on screen starts now, and each other page on its next
                visit. A page with no tour of its own (the draft room, the
                rules) sends them to Home, whose tour then starts by itself:
                closing the menu and showing nothing read as a dead button. */}
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                turnOnTours();
                if (!startActiveTour()) navigate('/home');
              }}
              style={{ width: '100%', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-bg-page)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <HelpCircle size={14} color="var(--color-text-muted)" />
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-strong)' }}>Show Tutorial</span>
            </button>

            <div style={{ height: 1, backgroundColor: 'var(--color-bg-subtle)', margin: '2px 0' }} />

            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              style={{ width: '100%', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 8, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-danger-bg)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <LogOut size={14} color="var(--color-danger-text)" />
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-danger-text)' }}>Logout</span>
            </button>
          </div>
        </div>,
        document.body
      )}

      {showEditModal && <EditProfileModal onClose={() => setShowEditModal(false)} />}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </>
  );
};

export default ProfileDropdown;
